import { cn } from "cn";
import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button/button";
import { Card } from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Skeleton } from "@/components/ui/skeleton";
import { PaymentMethodIcon } from "@/features/shared/billing/payments/_components/payment-method-icon";
import { PaymentMethodSetupDialog } from "@/features/shared/billing/payments/_components/payment-method-setup-dialog";
import { PaymentMethodType } from "@/features/shared/billing/payments/_types/payment-method-type";
import {
  type PresalePaymentMethod,
  usePresalePaymentMethods,
} from "../../_api/use-presale-payment-methods";

/**
 * The presale twin of the checkout's payment-method step, on the account's real cards.
 *
 * Adding one goes through the shared `PaymentMethodSetupDialog`, whose SetupIntent comes
 * from `POST /billing/payment-methods/setup-intent` — an account-level call that needs no
 * purchase intent and no place, and that fires only once the dialog is opened.
 *
 * Selection is NOT local: `selectedId` is the id stored on the `PurchaseIntent`, and
 * `onSelect` writes it back there. The server prices the cart from it — a credit card
 * carries a convenience fee and a bank account does not — so a selection held in React
 * state would quote a total the charge disagrees with.
 */
type PresalePaymentMethodSectionProps = {
  /** The buyer's account, from the purchase intent. Absent while it loads. */
  accountId: string | undefined;
  /** The card recorded on the intent — the server's own answer, not local state. */
  selectedId: string;
  /** `type` is `"card"` / `"us_bank_account"`, for the analytics `paymentMethod` key. */
  onSelect: (id: string, type?: string) => void;
  /** Fired once per card the buyer adds through the dialog. */
  onAdded?: (id: string, type?: string) => void;
  /**
   * The buyer's OWN change of selection.
   *
   * Separate from `onSelect` on purpose: `onSelect` also fires for the auto-preselect below
   * and for the card the dialog just added, and reporting either as "the buyer changed
   * card" would count data merely arriving as an interaction.
   */
  onChanged?: (id: string, type?: string) => void;
  /** A Stripe `confirmSetup` failure inside the shared dialog. */
  onAddFailed?: (message?: string) => void;
};

export function PresalePaymentMethodSection({
  accountId,
  selectedId,
  onSelect,
  onAdded,
  onChanged,
  onAddFailed,
}: PresalePaymentMethodSectionProps) {
  const { paymentMethods, isLoading, refetch } =
    usePresalePaymentMethods(accountId);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const hasAutoSelected = useRef(false);

  const typeOf = (method: PresalePaymentMethod | undefined) =>
    method ? (method.card ? "card" : "us_bank_account") : undefined;
  const typeOfId = (id: string) =>
    typeOf(paymentMethods.find((method) => method.id === id));

  // Preselect once, so Confirm is reachable without an extra click. Only fires when the
  // intent carries no card at all: `getPurchaseIntentById` already back-fills the account
  // default, so `selectedId` is usually set before this runs. Guarded against refetches —
  // a buyer's own choice must never be overwritten by arriving data.
  useEffect(() => {
    if (hasAutoSelected.current || selectedId || paymentMethods.length === 0) {
      return;
    }
    hasAutoSelected.current = true;
    const preferred =
      paymentMethods.find((method) => method.isDefault) ?? paymentMethods[0];
    onSelect(preferred.id, typeOf(preferred));
  }, [paymentMethods, selectedId, onSelect]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h3 className="text-base leading-normal font-medium mb-1">
            Add a payment method
          </h3>
          <p className="text-sm leading-5 font-normal text-text-dark-low">
            For today’s payment and the default for future billing. You can
            manage payment methods in your account at any time.
          </p>
        </div>
        <Button kind="ghost" size="sm" onClick={() => setIsAddOpen(true)}>
          Add
        </Button>
      </div>

      {isLoading ? (
        <div className="space-y-2">
          <Skeleton className="h-18 w-full rounded-2xl" />
          <Skeleton className="h-18 w-full rounded-2xl" />
        </div>
      ) : paymentMethods.length === 0 ? (
        <p className="text-sm leading-5 font-normal text-text-dark-low">
          No payment methods yet. Add one to reserve your spot.
        </p>
      ) : (
        <RadioGroup
          value={selectedId}
          onValueChange={(id) => {
            onSelect(id, typeOfId(id));
            onChanged?.(id, typeOfId(id));
          }}
          className="space-y-2 gap-0"
        >
          {paymentMethods.map((method) => (
            <PresalePaymentMethodOption
              key={method.id}
              method={method}
              isSelected={method.id === selectedId}
            />
          ))}
        </RadioGroup>
      )}

      <PaymentMethodSetupDialog
        open={isAddOpen}
        onOpenChange={setIsAddOpen}
        isFirstMethod={paymentMethods.length === 0}
        onSuccess={async ({ paymentMethodId }) => {
          // Supplying `onSuccess` short-circuits the dialog's own cache invalidation
          // AND its success screen, so the list refresh and the close are ours to do.
          // The type comes off the REFETCH result — the new card is not in the list the
          // render closed over.
          const fresh = await refetch();
          const added = fresh.find((method) => method.id === paymentMethodId);
          onSelect(paymentMethodId, typeOf(added));
          onAdded?.(paymentMethodId, typeOf(added));
          setIsAddOpen(false);
        }}
        onError={(error) => onAddFailed?.(error?.message)}
      />
    </div>
  );
}

/** One selectable saved method — a card, or a US bank account. */
function PresalePaymentMethodOption({
  method,
  isSelected,
}: {
  method: PresalePaymentMethod;
  isSelected: boolean;
}) {
  const isCard = Boolean(method.card);
  const brand = isCard
    ? (method.card?.brand ?? "")
    : (method.usBankAccount?.bankName ?? "");
  const last4 = isCard
    ? (method.card?.last4 ?? "")
    : (method.usBankAccount?.last4 ?? "");

  return (
    <Card
      className={cn(
        /* sorted */
        "p-4",
        "border border-border-dark-low rounded-2xl shadow-none",
        isSelected && "border-border-primary-max",
      )}
    >
      <label className="flex cursor-pointer items-center gap-2">
        <PaymentMethodIcon
          brand={brand}
          type={
            isCard ? PaymentMethodType.Card : PaymentMethodType.UsBankAccount
          }
          className="shrink-0"
        />
        <p className="grow text-sm font-medium">
          {last4 ? `**** **** **** ${last4}` : brand}
        </p>
        <RadioGroupItem value={method.id} />
      </label>
    </Card>
  );
}
